import { GLOBAL_LANGUAGES } from '@/app/constants';
import type { I18nRecord, SupportedLanguage } from '@/app/types';

/**
 * 动态生成语言配置对象 (用于页面路径)
 * @param path 页面路径
 * @param locales 支持的语言列表，默认使用 GLOBAL_LANGUAGES
 * @returns 语言配置对象，包含 x-default 和所有支持的语言
 */
export function generateLanguagesConfig(
  path: string,
  locales: SupportedLanguage[] = GLOBAL_LANGUAGES,
): Record<string, string> {
  const languages: Record<string, string> = {
    'x-default': path,
  };

  locales.forEach((lang: SupportedLanguage) => {
    if (lang === 'en') {
      languages[lang] = path;
    } else {
      languages[lang] = `/${lang}${path}`;
    }
  });

  return languages;
}

export function fillMissingTranslationsWithEnglish<T>(i18nRecord: I18nRecord<T>): I18nRecord<T> {
  const result = { ...i18nRecord };

  // 只处理 global 区域
  const globalRecord = result.global;
  if (!globalRecord) {
    return result;
  }

  // 获取英文翻译作为回退内容
  const englishTranslation = globalRecord.en;
  if (!englishTranslation) {
    return result;
  }

  // 检查所有支持的语言
  for (const language of GLOBAL_LANGUAGES) {
    if (language === 'en') {
      continue;
    }

    const translation = globalRecord[language as SupportedLanguage];

    // 检查翻译是否缺失或为空
    if (isTranslationMissingOrEmpty(translation)) {
      globalRecord[language as SupportedLanguage] = englishTranslation;
    }
  }

  return result;
}

/**
 * 检查翻译内容是否缺失或为空
 * @param translation - 待检查的翻译内容
 * @returns 是否缺失或为空
 */
function isTranslationMissingOrEmpty<T>(translation: T | undefined): boolean {
  if (translation === undefined || translation === null) {
    return true;
  }

  if (Array.isArray(translation)) {
    return translation.length === 0;
  }

  if (typeof translation === 'string') {
    return translation.trim() === '';
  }

  if (typeof translation === 'object') {
    return Object.keys(translation).length === 0;
  }

  return false;
}

/**
 * 创建多语言配置的便捷函数
 * 自动为 global 区域的所有语言生成配置，缺失的翻译会用英文填充
 *
 * @param translations - 包含各语言翻译的对象
 * @param chinaTranslation - 中国区域的翻译
 * @param useChineseForGlobal - 是否让 global.zh 也使用 chinaTranslation，默认为 true
 * @returns 完整的 I18nRecord 配置
 *
 * @example
 * ```ts
 * // 情况1: china 和 global 的中文相同（默认行为）
 * const TIPS = createI18nConfig({
 *   en: ['English tips'],
 *   // zh 会自动使用 chinaTranslation
 * }, ['中文提示']);
 *
 * // 情况2: china 和 global 的中文不同
 * const TIPS2 = createI18nConfig({
 *   en: ['English tips'],
 *   zh: ['全球中文提示'], // 明确指定 global 的中文
 * }, ['中国中文提示'], false);
 *
 * // 情况3: 部分相同，部分不同
 * const TIPS3 = createI18nConfig({
 *   en: ['English tips'],
 *   zh: ['全球中文提示'], // 会覆盖自动设置的值
 * }, ['中国中文提示']); // useChineseForGlobal 默认为 true，但会被 translations.zh 覆盖
 * ```
 */
export function createI18nConfig<T>(
  translations: Partial<Record<SupportedLanguage, T>>,
  chinaTranslation: T,
  useChineseForGlobal = true,
): I18nRecord<T> {
  const globalTranslations = { ...translations };

  // 如果 useChineseForGlobal 为 true 且 translations 中没有指定 zh，则使用 chinaTranslation
  if (useChineseForGlobal && !globalTranslations.zh) {
    globalTranslations.zh = chinaTranslation;
  }

  const config: I18nRecord<T> = {
    china: {
      zh: chinaTranslation,
    },
    global: globalTranslations,
  };

  return fillMissingTranslationsWithEnglish(config);
}
